import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, useInView } from "framer-motion";
import { buildLocalizedPath } from "../constants/languages";
import StatCounter from "../components/StatCounter";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  Line,
} from "react-simple-maps";
import {
  Shield,
  Globe,
  FileCheck,
  Recycle,
  Award,
  Building2,
  TrendingUp,
  Users,
  Ship,
  Handshake,
} from "lucide-react";
import { ChevronRight } from "../components/DirectionalIcon";
import { COMPANY_STATS } from "../constants/stats";

import heroBg from "../assets/hero-bg.jpg";
import containerLoadingBg from "../assets/container-loading.jpg";

const HERO_BG = heroBg;
const CORPORATE_BANNER_BG = containerLoadingBg;
const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const WorldMap = () => {
  const { t } = useTranslation();
  const [hoveredMarker, setHoveredMarker] = useState(null);

  const offices = [
    { name: "South Korea", coordinates: [127.5, 37.5], type: t("home.globalReach.map.headOffice") },
    { name: "United Kingdom", coordinates: [-1.5, 52.5], type: t("home.globalReach.map.ukOffice") },
  ];

  const supplySources = [
    { name: "Germany", coordinates: [10.5, 51.2] },
    { name: "France", coordinates: [2.3, 46.2] },
    { name: "Spain", coordinates: [-3.7, 40.4] },
    { name: "Italy", coordinates: [12.5, 41.9] },
    { name: "Portugal", coordinates: [-8.2, 39.4] },
    { name: "Netherlands", coordinates: [5.3, 52.1] },
    { name: "Belgium", coordinates: [4.5, 50.5] },
    { name: "Denmark", coordinates: [10.0, 56.0] },
    { name: "Sweden", coordinates: [18.1, 59.3] },
    { name: "Norway", coordinates: [10.7, 59.9] },
    { name: "Finland", coordinates: [25.7, 61.9] },
    { name: "Switzerland", coordinates: [8.2, 46.8] },
    { name: "Austria", coordinates: [14.6, 47.8] },
    { name: "Poland", coordinates: [19.9, 52.2] },
    { name: "Czech Republic", coordinates: [15.5, 49.8] },
    { name: "Hungary", coordinates: [19.0, 47.2] },
    { name: "Romania", coordinates: [24.9, 45.9] },
    { name: "Greece", coordinates: [21.8, 39.1] },
    { name: "Ireland", coordinates: [-8.2, 53.4] },
    { name: "USA", coordinates: [-99.1, 39.5] },
    { name: "Canada", coordinates: [-96.8, 56.1] },
    { name: "Mexico", coordinates: [-102.5, 23.6] },
    { name: "Brazil", coordinates: [-51.9, -14.2] },
    { name: "Argentina", coordinates: [-63.6, -38.4] },
    { name: "Chile", coordinates: [-71.5, -35.7] },
    { name: "Colombia", coordinates: [-74.3, 4.6] },
    { name: "Peru", coordinates: [-75.0, -9.2] },
    { name: "Uruguay", coordinates: [-56.0, -32.5] },
    { name: "Japan", coordinates: [138.0, 36.2] },
    { name: "Australia", coordinates: [133.8, -25.7] },
    { name: "New Zealand", coordinates: [172.5, -41.3] },
    { name: "Israel", coordinates: [34.9, 31.5] },
  ];

  const buyerMarkets = [
    { name: "Pakistan", coordinates: [69.3, 30.4] },
    { name: "Bangladesh", coordinates: [90.4, 23.7] },
    { name: "India", coordinates: [78.9, 20.6] },
    { name: "UAE", coordinates: [54.4, 24.0] },
    { name: "Saudi Arabia", coordinates: [45.1, 24.7] },
    { name: "Nigeria", coordinates: [8.7, 9.1] },
    { name: "Ghana", coordinates: [-1.0, 7.9] },
    { name: "Kenya", coordinates: [37.9, -1.3] },
    { name: "Egypt", coordinates: [30.8, 26.8] },
    { name: "South Africa", coordinates: [25.1, -29.0] },
    { name: "Malaysia", coordinates: [109.7, 4.2] },
    { name: "Sri Lanka", coordinates: [80.7, 7.9] },
    { name: "Afghanistan", coordinates: [67.7, 33.9] },
    { name: "Kazakhstan", coordinates: [66.9, 48.0] },
    { name: "Uzbekistan", coordinates: [63.9, 41.4] },
    { name: "Turkmenistan", coordinates: [58.4, 38.0] },
    { name: "Kyrgyzstan", coordinates: [74.6, 41.2] },
    { name: "Tajikistan", coordinates: [71.3, 38.9] },
    { name: "Azerbaijan", coordinates: [47.6, 40.1] },
    { name: "Iraq", coordinates: [43.7, 33.2] },
    { name: "Iran", coordinates: [53.7, 32.4] },
    { name: "Jordan", coordinates: [36.2, 31.2] },
    { name: "Kuwait", coordinates: [47.5, 29.3] },
    { name: "Qatar", coordinates: [51.2, 25.3] },
    { name: "Bahrain", coordinates: [50.6, 26.2] },
    { name: "Oman", coordinates: [57.5, 21.5] },
    { name: "Yemen", coordinates: [48.5, 15.6] },
    { name: "Syria", coordinates: [38.3, 34.8] },
    { name: "Lebanon", coordinates: [35.9, 33.9] },
    { name: "Turkey", coordinates: [35.2, 39.0] },
    { name: "China", coordinates: [104.2, 35.9] },
    { name: "Indonesia", coordinates: [113.9, -0.8] },
    { name: "Philippines", coordinates: [121.8, 12.9] },
    { name: "Vietnam", coordinates: [108.3, 14.1] },
    { name: "Thailand", coordinates: [100.9, 15.9] },
    { name: "Myanmar", coordinates: [95.9, 19.2] },
    { name: "Cambodia", coordinates: [104.9, 12.6] },
    { name: "Nepal", coordinates: [84.1, 28.4] },
    { name: "Mongolia", coordinates: [103.8, 46.9] },
    { name: "Laos", coordinates: [102.5, 18.2] },
    { name: "Morocco", coordinates: [-7.1, 31.8] },
    { name: "Algeria", coordinates: [2.6, 28.0] },
    { name: "Tunisia", coordinates: [9.6, 33.9] },
    { name: "Libya", coordinates: [17.2, 27.0] },
    { name: "Sudan", coordinates: [30.2, 15.6] },
    { name: "Ethiopia", coordinates: [40.5, 9.1] },
    { name: "Tanzania", coordinates: [34.9, -6.4] },
    { name: "Uganda", coordinates: [32.3, 1.4] },
    { name: "Mozambique", coordinates: [35.0, -18.7] },
    { name: "Zambia", coordinates: [27.8, -13.1] },
    { name: "Zimbabwe", coordinates: [29.9, -20.0] },
    { name: "Angola", coordinates: [17.9, -11.2] },
    { name: "Cameroon", coordinates: [12.4, 5.7] },
    { name: "Ivory Coast", coordinates: [-5.6, 7.5] },
    { name: "Senegal", coordinates: [-14.5, 14.5] },
    { name: "Mali", coordinates: [-2.0, 17.6] },
    { name: "Burkina Faso", coordinates: [-1.6, 12.4] },
    { name: "Niger", coordinates: [8.1, 17.6] },
    { name: "Chad", coordinates: [18.7, 15.5] },
    { name: "Somalia", coordinates: [45.3, 5.2] },
    { name: "Madagascar", coordinates: [46.9, -19.4] },
    { name: "Malawi", coordinates: [34.3, -13.3] },
    { name: "Rwanda", coordinates: [29.9, -1.9] },
    { name: "Democratic Republic of Congo", coordinates: [23.7, -2.9] },
  ];

  const tradeRoutes = [
    { from: [-1.5, 52.5], to: [8.7, 9.1] },
    { from: [-1.5, 52.5], to: [-1.0, 7.9] },
    { from: [-1.5, 52.5], to: [37.9, -1.3] },
    { from: [-1.5, 52.5], to: [25.1, -29.0] },
    { from: [-1.5, 52.5], to: [69.3, 30.4] },
    { from: [-1.5, 52.5], to: [90.4, 23.7] },
    { from: [-1.5, 52.5], to: [78.9, 20.6] },
    { from: [-1.5, 52.5], to: [54.4, 24.0] },
    { from: [-1.5, 52.5], to: [45.1, 24.7] },
    { from: [-1.5, 52.5], to: [30.8, 26.8] },
    { from: [127.5, 37.5], to: [109.7, 4.2] },
    { from: [127.5, 37.5], to: [80.7, 7.9] },
    { from: [127.5, 37.5], to: [69.3, 30.4] },
    { from: [127.5, 37.5], to: [90.4, 23.7] },
    { from: [-1.5, 52.5], to: [-7.1, 31.8] },
    { from: [-1.5, 52.5], to: [2.6, 28.0] },
    { from: [-1.5, 52.5], to: [9.6, 33.9] },
    { from: [-1.5, 52.5], to: [35.2, 39.0] },
    { from: [-1.5, 52.5], to: [43.7, 33.2] },
    { from: [-1.5, 52.5], to: [53.7, 32.4] },
    { from: [-1.5, 52.5], to: [40.5, 9.1] },
    { from: [-1.5, 52.5], to: [34.9, -6.4] },
    { from: [-1.5, 52.5], to: [35.0, -18.7] },
    { from: [127.5, 37.5], to: [104.2, 35.9] },
    { from: [127.5, 37.5], to: [113.9, -0.8] },
    { from: [127.5, 37.5], to: [121.8, 12.9] },
    { from: [127.5, 37.5], to: [108.3, 14.1] },
    { from: [127.5, 37.5], to: [100.9, 15.9] },
    { from: [127.5, 37.5], to: [95.9, 19.2] },
    { from: [127.5, 37.5], to: [66.9, 48.0] },
    { from: [127.5, 37.5], to: [63.9, 41.4] },
    { from: [127.5, 37.5], to: [103.8, 46.9] },
    { from: [-51.9, -14.2], to: [8.7, 9.1] },
    { from: [-51.9, -14.2], to: [-1.0, 7.9] },
    { from: [-51.9, -14.2], to: [25.1, -29.0] },
    { from: [-63.6, -38.4], to: [54.4, 24.0] },
    { from: [-63.6, -38.4], to: [69.3, 30.4] },
    { from: [-71.5, -35.7], to: [109.7, 4.2] },
    { from: [-71.5, -35.7], to: [80.7, 7.9] },
    { from: [-102.5, 23.6], to: [69.3, 30.4] },
    { from: [-102.5, 23.6], to: [54.4, 24.0] },
  ];

  return (
    <div className="relative w-full rounded-xl border border-slate-800 overflow-hidden" style={{ aspectRatio: "2/1", background: "#0B1629" }}>
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{
          scale: 115,
          center: [30, 20],
        }}
        style={{ width: "100%", height: "100%" }}
      >
        <Geographies geography={GEO_URL}>
          {({ geographies }) =>
            geographies.map((geo) => (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                fill="#1E293B"
                stroke="#334155"
                strokeWidth={0.5}
                style={{
                  default: { outline: "none" },
                  hover: { fill: "#263548", outline: "none" },
                  pressed: { outline: "none" },
                }}
              />
            ))
          }
        </Geographies>

        {tradeRoutes.map((route, i) => (
          <Line
            key={`route-${i}`}
            coordinates={[route.from, route.to]}
            stroke="#C5A059"
            strokeWidth={1}
            strokeOpacity={0.6}
            strokeDasharray="4,3"
            strokeLinecap="round"
          />
        ))}

        {supplySources.map((source) => (
          <Marker
            key={source.name}
            coordinates={source.coordinates}
            onMouseEnter={() => setHoveredMarker({ ...source, type: t("home.globalReach.map.supplySource") })}
            onMouseLeave={() => setHoveredMarker(null)}
          >
            <circle r={6} fill="#475569" stroke="#64748B" strokeWidth={1.5} style={{ cursor: "pointer" }} />
          </Marker>
        ))}

        {buyerMarkets.map((market) => (
          <Marker
            key={market.name}
            coordinates={market.coordinates}
            onMouseEnter={() => setHoveredMarker({ ...market, type: t("home.globalReach.map.buyerMarket") })}
            onMouseLeave={() => setHoveredMarker(null)}
          >
            <circle r={3} fill="#D97706" stroke="#F59E0B" strokeWidth={1} style={{ cursor: "pointer" }} />
          </Marker>
        ))}

        {offices.map((office) => (
          <Marker
            key={office.name}
            coordinates={office.coordinates}
            onMouseEnter={() => setHoveredMarker(office)}
            onMouseLeave={() => setHoveredMarker(null)}
          >
            <circle r={20} fill="none" stroke="#C5A059" strokeWidth={2} strokeOpacity={0.6} className="animate-ping" style={{ animationDuration: "2s" }} />
            <circle r={16} fill="#C5A059" fillOpacity={0.2} />
            <circle r={10} fill="#C5A059" stroke="#E5C585" strokeWidth={2} style={{ cursor: "pointer" }} />
          </Marker>
        ))}
      </ComposableMap>

      {hoveredMarker && (
        <div className="absolute top-4 start-4 bg-navy border border-slate-700 px-4 py-2 rounded-lg shadow-lg z-10">
          <span className="text-white font-semibold">{hoveredMarker.name}</span>
          <span className="text-gold text-sm ms-2">• {hoveredMarker.type}</span>
        </div>
      )}
    </div>
  );
};

const ComplianceBadge = ({ icon: Icon, title, subtitle, description }) => (
  <div className="compliance-badge bg-white border border-slate-200 p-6 transition-all cursor-default">
    <div className="flex items-start gap-4">
      <div className="p-3 bg-navy text-gold rounded-sm">
        <Icon size={24} strokeWidth={1.5} />
      </div>
      <div>
        <h4 className="font-outfit font-semibold text-navy">{title}</h4>
        {subtitle && <p className="text-gold text-sm font-medium">{subtitle}</p>}
        <p className="text-slate-500 text-sm mt-2">{description}</p>
      </div>
    </div>
  </div>
);

const HomePage = () => {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    document.title = t("home.meta.title");
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", t("home.meta.description"));
    }
  }, [t]);

  const statsRef = useRef(null);
  const statsInView = useInView(statsRef, { once: true, margin: "-100px" });

  const stats = [
    { value: 26, suffix: "+", label: t("home.stats.yearsExperience"), icon: Building2 },
    { value: 2186, suffix: "+", label: t("home.stats.worldwideClients"), icon: Users },
    { value: 2899, suffix: "+", label: t("home.stats.successfulProjects"), icon: TrendingUp },
    { value: 1000, suffix: "+", label: t("home.stats.containersShipped"), icon: Ship },
    { value: 3112, suffix: "+", label: t("home.stats.importExportTransactions"), icon: Globe },
    { value: 24, suffix: "+", label: t("home.stats.manufacturerPartners"), icon: Handshake },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        >
          <div className="absolute inset-0 hero-gradient"></div>
        </div>

        <div className="relative z-10 container-custom text-center pt-32 pb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block text-gold text-xs uppercase tracking-[0.3em] font-semibold mb-6">
              {t("home.hero.kicker")}
            </span>
            <h1 className="font-outfit text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6">
              {t("home.hero.title")}<br />
              <span className="gold-shimmer">{t("home.hero.titleAccent")}</span>
            </h1>
            <p className="text-slate-300 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
              {t("home.hero.subtitle")}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to={buildLocalizedPath("/sell-panels", i18n.language)}
                data-testid="hero-sell-panels-btn"
                className="btn-magnetic w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gold text-navy px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-gold-light transition-colors"
              >
                {t("home.hero.ctaSell")}
                <ChevronRight size={16} />
              </Link>
              <Link
                to={buildLocalizedPath("/buy-panels", i18n.language)}
                data-testid="hero-buy-panels-btn"
                className="btn-magnetic w-full sm:w-auto inline-flex items-center justify-center gap-2 border-2 border-gold text-gold px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-gold hover:text-navy transition-all"
              >
                {t("home.hero.ctaBuy")}
                <ChevronRight size={16} />
              </Link>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1 h-3 bg-gold rounded-full"
            />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section ref={statsRef} className="py-20 md:py-32 bg-slate-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={statsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-gold text-xs uppercase tracking-[0.2em] font-semibold">
              {t("home.stats.kicker")}
            </span>
            <h2 className="font-outfit text-3xl md:text-4xl lg:text-5xl font-bold text-navy mt-4">
              {t("home.stats.title")}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={statsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <StatCounter {...stat} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Corporate Partners Banner — English-only, see CorporatePartnersPage for why.
          Full-bleed background-image treatment reusing the exact same pattern as the
          hero above (hero-gradient overlay + bg-cover bg-center), just with
          container-loading.jpg instead of hero-bg.jpg — kept distinct from
          rooftop-scale.jpg, which is already CorporatePartnersPage's own hero image. */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${CORPORATE_BANNER_BG})` }}
        >
          <div className="absolute inset-0 hero-gradient"></div>
        </div>

        <div className="relative z-10 container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-outfit text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Decommissioning or Repowering a Solar Site?
            </h2>
            <p className="text-slate-300 max-w-2xl mx-auto mb-10 text-lg leading-relaxed">
              M A Galaxy International purchases surplus, used, and decommissioned solar panels from solar farms,
              EPC contractors, O&amp;M providers, and insurance claims — managing collection, logistics, and export
              under standard commercial trade terms. Get a formal quote for your inventory.
            </p>
            <Link
              to={buildLocalizedPath("/corporate-partners", i18n.language)}
              data-testid="cta-corporate-partners"
              className="btn-magnetic inline-flex items-center justify-center gap-2 bg-gold text-navy px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-gold-light transition-colors"
            >
              Request a Trade Quote
              <ChevronRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Who We Serve Section */}
      <section className="py-20 md:py-32">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-gold text-xs uppercase tracking-[0.2em] font-semibold">
              {t("home.whoWeServe.kicker")}
            </span>
            <h2 className="font-outfit text-3xl md:text-4xl lg:text-5xl font-bold text-navy mt-4">
              {t("home.whoWeServe.title")}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* For Suppliers */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-navy p-8 md:p-10 relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gold/10 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="relative z-10">
                <span className="inline-block px-3 py-1 bg-gold text-navy text-xs font-bold uppercase tracking-wider mb-6">
                  {t("home.whoWeServe.suppliers.badge")}
                </span>
                <h3 className="font-outfit text-2xl md:text-3xl font-bold text-white mb-4">
                  {t("home.whoWeServe.suppliers.title")}
                </h3>
                <p className="text-slate-300 mb-6 leading-relaxed">
                  {t("home.whoWeServe.suppliers.description")}
                </p>
                <ul className="space-y-3 mb-8">
                  {[
                    t("home.whoWeServe.suppliers.bullet1"),
                    t("home.whoWeServe.suppliers.bullet2"),
                    t("home.whoWeServe.suppliers.bullet3"),
                    t("home.whoWeServe.suppliers.bullet4"),
                    t("home.whoWeServe.suppliers.bullet5"),
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-300">
                      <span className="text-gold">✓</span>
                      {item}
                    </li>
                  ))}
                </ul>
                <Link
                  to={buildLocalizedPath("/sell-panels", i18n.language)}
                  data-testid="who-we-serve-sell-btn"
                  className="inline-flex items-center gap-2 bg-gold text-navy px-6 py-3 text-xs font-bold uppercase tracking-widest hover:bg-gold-light transition-colors"
                >
                  {t("home.whoWeServe.suppliers.cta")}
                  <ChevronRight size={16} />
                </Link>
              </div>
            </motion.div>

            {/* For Buyers */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-slate-800 p-8 md:p-10 relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gold/10 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="relative z-10">
                <span className="inline-block px-3 py-1 bg-gold text-navy text-xs font-bold uppercase tracking-wider mb-6">
                  {t("home.whoWeServe.buyers.badge")}
                </span>
                <h3 className="font-outfit text-2xl md:text-3xl font-bold text-white mb-4">
                  {t("home.whoWeServe.buyers.title")}
                </h3>
                <p className="text-slate-300 mb-6 leading-relaxed">
                  {t("home.whoWeServe.buyers.description")}
                </p>
                <ul className="space-y-3 mb-8">
                  {[
                    t("home.whoWeServe.buyers.bullet1"),
                    t("home.whoWeServe.buyers.bullet2"),
                    t("home.whoWeServe.buyers.bullet3"),
                    t("home.whoWeServe.buyers.bullet4"),
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-300">
                      <span className="text-gold">✓</span>
                      {item}
                    </li>
                  ))}
                </ul>
                <Link
                  to={buildLocalizedPath("/buy-panels", i18n.language)}
                  data-testid="who-we-serve-buy-btn"
                  className="inline-flex items-center gap-2 bg-gold text-navy px-6 py-3 text-xs font-bold uppercase tracking-widest hover:bg-gold-light transition-colors"
                >
                  {t("home.whoWeServe.buyers.cta")}
                  <ChevronRight size={16} />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Two Operations Section */}
      <section className="py-20 md:py-32">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-gold text-xs uppercase tracking-[0.2em] font-semibold">
              {t("home.operations.kicker")}
            </span>
            <h2 className="font-outfit text-3xl md:text-4xl lg:text-5xl font-bold text-navy mt-4">
              {t("home.operations.title")}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* UK Operations */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="service-card bg-white border border-slate-200 p-8 md:p-12"
            >
              <div className="inline-block px-4 py-2 bg-navy text-gold text-xs font-bold uppercase tracking-wider mb-6">
                {t("home.operations.uk.badge")}
              </div>
              <h3 className="font-outfit text-2xl md:text-3xl font-bold text-navy mb-4">
                {t("home.operations.uk.title")}
              </h3>
              <p className="text-slate-600 mb-6 leading-relaxed">
                {t("home.operations.uk.description")}
              </p>
              <ul className="space-y-3 mb-8 text-slate-600">
                {[
                  t("home.operations.uk.bullet1"),
                  t("home.operations.uk.bullet2"),
                  t("home.operations.uk.bullet3"),
                  t("home.operations.uk.bullet4"),
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <span className="w-1.5 h-1.5 bg-gold rounded-full"></span>
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to={buildLocalizedPath("/sell-panels", i18n.language)}
                data-testid="uk-operations-cta"
                className="inline-flex items-center gap-2 text-gold font-semibold hover:gap-4 transition-all"
              >
                {t("home.operations.uk.cta")} <ChevronRight size={18} />
              </Link>
            </motion.div>

            {/* Korea Operations */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="service-card bg-navy text-white p-8 md:p-12"
            >
              <div className="inline-block px-4 py-2 bg-gold text-navy text-xs font-bold uppercase tracking-wider mb-6">
                {t("home.operations.korea.badge")}
              </div>
              <h3 className="font-outfit text-2xl md:text-3xl font-bold mb-4">
                {t("home.operations.korea.title")}
              </h3>
              <p className="text-slate-300 mb-6 leading-relaxed">
                {t("home.operations.korea.description")}
              </p>
              <ul className="space-y-3 mb-8 text-slate-300">
                {[
                  t("home.operations.korea.bullet1"),
                  t("home.operations.korea.bullet2"),
                  t("home.operations.korea.bullet3"),
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <span className="w-1.5 h-1.5 bg-gold rounded-full"></span>
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to={buildLocalizedPath("/korea-operations", i18n.language)}
                data-testid="korea-operations-cta"
                className="inline-flex items-center gap-2 text-gold font-semibold hover:gap-4 transition-all"
              >
                {t("home.operations.korea.cta")} <ChevronRight size={18} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Compliance Section Preview */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-gold text-xs uppercase tracking-[0.2em] font-semibold">
              {t("home.compliance.kicker")}
            </span>
            <h2 className="font-outfit text-3xl md:text-4xl lg:text-5xl font-bold text-navy mt-4 mb-4">
              {t("home.compliance.title")}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {t("home.compliance.subtitle")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <ComplianceBadge
              icon={Shield}
              title={t("home.compliance.badges.environmentAgency.title")}
              subtitle={t("home.compliance.badges.environmentAgency.subtitle")}
              description={t("home.compliance.badges.environmentAgency.description")}
            />
            <ComplianceBadge
              icon={FileCheck}
              title={t("home.compliance.badges.wasteTransferNotes.title")}
              description={t("home.compliance.badges.wasteTransferNotes.description")}
            />
            <ComplianceBadge
              icon={Recycle}
              title={t("home.compliance.badges.reuseCommitment.title")}
              description={t("home.compliance.badges.reuseCommitment.description")}
            />
          </div>

          <div className="text-center mt-12">
            <Link
              to={buildLocalizedPath("/compliance", i18n.language)}
              data-testid="compliance-learn-more"
              className="inline-flex items-center gap-2 bg-navy text-white px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-navy-900 transition-colors"
            >
              {t("home.compliance.cta")}
              <ChevronRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Global Presence Section */}
      <section className="py-20 md:py-32 bg-navy text-white relative noise-overlay">
        <div className="container-custom relative z-10">
          <div className="text-center mb-16">
            <span className="text-gold text-xs uppercase tracking-[0.2em] font-semibold">
              {t("home.globalReach.kicker")}
            </span>
            <h2 className="font-outfit text-3xl md:text-4xl lg:text-5xl font-bold mt-4">
              {t("home.globalReach.title")}
            </h2>
            <p className="text-slate-400 mt-4 max-w-2xl mx-auto">
              {t("home.globalReach.subtitle")}
            </p>
          </div>

          <WorldMap />

          <div className="mt-8 flex flex-wrap justify-center items-center gap-8 text-sm">
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded-full" style={{ backgroundColor: "#C5A059" }}></span>
              <span className="text-slate-300">{t("home.globalReach.legendOffices")}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded-full" style={{ backgroundColor: "#475569" }}></span>
              <span className="text-slate-300">{t("home.globalReach.legendSupply")}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 rounded-full" style={{ backgroundColor: "#D97706" }}></span>
              <span className="text-slate-300">{t("home.globalReach.legendBuyers")}</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="container-custom">
          <div className="bg-white border border-slate-200 p-8 md:p-16 text-center">
            <h2 className="font-outfit text-3xl md:text-4xl lg:text-5xl font-bold text-navy mb-6">
              {t("home.cta.title")}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto mb-10 text-lg">
              {t("home.cta.subtitle")}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to={buildLocalizedPath("/sell-panels", i18n.language)}
                data-testid="cta-sell-panels"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gold text-navy px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-gold-light transition-colors"
              >
                {t("home.cta.ctaSell")}
                <ChevronRight size={16} />
              </Link>
              <Link
                to={buildLocalizedPath("/contact", i18n.language)}
                data-testid="cta-contact"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-navy text-white px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-navy-900 transition-colors"
              >
                {t("home.cta.ctaContact")}
                <ChevronRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
